import { useEffect, useState } from 'react'

type Effeh = {
  id: string
  status: 'candidate' | 'playable' | 'rejected'
  line_ar: string
  context_ar: string | null
  movie_title_ar: string
  movie_year: number
  movie_kind: string
  actor_name_ar: string
  character_name_ar: string | null
  frame_r2_key: string | null
  source_url: string
  source_type: string
  verified: number
  nostalgia_score: number | null
  notes: string | null
  created_at: number
}

type Stats = {
  by_status: Record<string, number>
  playable_unique_movies: number
  playable_unique_actors: number
  playable_with_frames: number
}

const PW_KEY = 'effeh:curator_pw'

export default function Curate() {
  const [pw, setPw] = useState<string>(() => localStorage.getItem(PW_KEY) ?? '')
  const [authed, setAuthed] = useState(false)
  const [authErr, setAuthErr] = useState<string | null>(null)

  async function tryLogin(candidate: string) {
    setAuthErr(null)
    const res = await fetch('/api/curator/me', { headers: { 'x-curator-password': candidate } })
    if (res.ok) {
      localStorage.setItem(PW_KEY, candidate)
      setPw(candidate)
      setAuthed(true)
    } else {
      setAuthErr(res.status === 401 ? 'كلمة سر غلط' : `خطأ ${res.status}`)
    }
  }

  // Auto-login if password is already stored
  useEffect(() => {
    if (pw) tryLogin(pw)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!authed) {
    return (
      <Shell>
        <div className="max-w-sm mx-auto mt-12">
          <h1 className="text-2xl mb-4 text-amber-300">دخول كيوريتور</h1>
          <input
            type="password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && tryLogin(pw)}
            placeholder="كلمة السر"
            className="w-full px-4 py-3 rounded-xl bg-ink-700/40 border border-ink-700 text-ink-50"
          />
          {authErr && <p className="text-rose-400 mt-2 text-sm">{authErr}</p>}
          <button
            onClick={() => tryLogin(pw)}
            className="w-full mt-3 py-3 rounded-full bg-amber-500 text-ink-900 font-bold"
          >
            ادخل
          </button>
        </div>
      </Shell>
    )
  }

  return <CuratorDashboard pw={pw} />
}

function CuratorDashboard({ pw }: { pw: string }) {
  const [tab, setTab] = useState<'candidate' | 'playable' | 'rejected'>('candidate')
  const [stats, setStats] = useState<Stats | null>(null)
  const [rows, setRows] = useState<Effeh[]>([])
  const [showImport, setShowImport] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const headers = { 'x-curator-password': pw, 'content-type': 'application/json' }

  async function refresh() {
    const [s, r] = await Promise.all([
      fetch('/api/curator/stats', { headers }).then((r) => r.json() as Promise<Stats>),
      fetch(`/api/curator/effehat?status=${tab}`, { headers }).then((r) => r.json() as Promise<{ effehat: Effeh[] }>),
    ])
    setStats(s)
    setRows(r.effehat)
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  async function patch(id: string, body: Partial<Effeh>) {
    const res = await fetch(`/api/curator/effehat/${id}`, {
      method: 'PATCH', headers, body: JSON.stringify(body),
    })
    if (!res.ok) alert('فشل التعديل')
    else refresh()
  }

  async function reject(id: string) {
    if (!confirm('متأكد إنك عاوز ترفض ده؟')) return
    const res = await fetch(`/api/curator/effehat/${id}`, { method: 'DELETE', headers })
    if (!res.ok) alert('فشل الرفض')
    else refresh()
  }

  async function uploadFrame(id: string, file: File) {
    const fd = new FormData()
    fd.append('frame', file)
    const res = await fetch(`/api/curator/effehat/${id}/frame`, {
      method: 'POST', headers: { 'x-curator-password': pw }, body: fd,
    })
    if (!res.ok) alert('فشل رفع الصورة')
    else refresh()
  }

  return (
    <Shell>
      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6 text-center text-sm">
          <Stat label="مرشحون" value={stats.by_status.candidate ?? 0} />
          <Stat label="منشورون" value={stats.by_status.playable ?? 0} />
          <Stat label="أفلام/مسلسلات" value={stats.playable_unique_movies} />
          <Stat label="بصور" value={stats.playable_with_frames} />
        </div>
      )}

      {/* Tabs + actions */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-1">
          {(['candidate', 'playable', 'rejected'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setTab(s)}
              className={`px-4 py-2 rounded-full text-sm ${
                tab === s ? 'bg-amber-500 text-ink-900' : 'bg-ink-700/40 text-ink-300'
              }`}
            >
              {s === 'candidate' ? 'مرشحون' : s === 'playable' ? 'منشورون' : 'مرفوضون'}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreate(true)}
            className="px-3 py-2 rounded-lg bg-ink-700/40 text-amber-300 text-sm"
          >
            + إفيه جديد
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="px-3 py-2 rounded-lg bg-ink-700/40 text-amber-300 text-sm"
          >
            استيراد CSV
          </button>
        </div>
      </div>

      {/* Rows */}
      <div className="space-y-3">
        {rows.length === 0 && <p className="text-ink-500 text-center py-12">فاضي.</p>}
        {rows.map((r) => (
          <Row key={r.id} row={r} onPatch={(b) => patch(r.id, b)} onReject={() => reject(r.id)} onFrame={(f) => uploadFrame(r.id, f)} />
        ))}
      </div>

      {showImport && (
        <Modal onClose={() => setShowImport(false)} title="استيراد CSV">
          <ImportCsv pw={pw} onDone={() => { setShowImport(false); refresh() }} />
        </Modal>
      )}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="إفيه جديد">
          <CreateNew pw={pw} onDone={() => { setShowCreate(false); refresh() }} />
        </Modal>
      )}
    </Shell>
  )
}

function Row({
  row, onPatch, onReject, onFrame,
}: {
  row: Effeh
  onPatch: (b: Partial<Effeh>) => void
  onReject: () => void
  onFrame: (f: File) => void
}) {
  const [edit, setEdit] = useState(false)
  const [draft, setDraft] = useState(row)

  function save() {
    const diff: Partial<Effeh> = {}
    const keys: (keyof Effeh)[] = ['line_ar', 'context_ar', 'movie_title_ar', 'movie_year', 'movie_kind', 'actor_name_ar', 'character_name_ar', 'source_url', 'nostalgia_score', 'notes']
    for (const k of keys) {
      if (draft[k] !== row[k]) (diff as Record<string, unknown>)[k] = draft[k]
    }
    if (Object.keys(diff).length === 0) { setEdit(false); return }
    onPatch(diff)
    setEdit(false)
  }

  return (
    <div className="border border-ink-700 rounded-2xl p-4 bg-ink-700/20">
      {!edit ? (
        <>
          <p className="text-lg font-display effeh-quote mb-2">{row.line_ar}</p>
          <p className="text-sm text-ink-300">
            <span className="text-amber-300">{row.movie_title_ar}</span>
            {' · '}{row.movie_year} · {row.actor_name_ar}
            {row.character_name_ar && <span className="text-ink-500"> ({row.character_name_ar})</span>}
          </p>
          {row.notes && <p className="text-xs text-rose-400 mt-1">⚠ {row.notes}</p>}
          <div className="flex gap-2 mt-3 flex-wrap text-sm">
            {row.status !== 'playable' && (
              <button onClick={() => onPatch({ status: 'playable' })} className="px-3 py-1 rounded-lg bg-leaf-600/30 text-leaf-400">انشر ✓</button>
            )}
            {row.status === 'playable' && (
              <button onClick={() => onPatch({ status: 'candidate' })} className="px-3 py-1 rounded-lg bg-ink-700 text-ink-300">رجّع للمسودة</button>
            )}
            <button onClick={() => setEdit(true)} className="px-3 py-1 rounded-lg bg-ink-700 text-ink-300">عدّل</button>
            <label className="px-3 py-1 rounded-lg bg-ink-700 text-ink-300 cursor-pointer">
              {row.frame_r2_key ? 'غيّر الصورة' : '+ صورة'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFrame(f) }}
              />
            </label>
            {row.frame_r2_key && (
              <a href={`/api/frame/${row.id}`} target="_blank" rel="noreferrer" className="px-3 py-1 rounded-lg bg-ink-700 text-amber-300">معاينة الصورة</a>
            )}
            <a href={row.source_url} target="_blank" rel="noreferrer" className="px-3 py-1 rounded-lg bg-ink-700 text-amber-300">المصدر ↗</a>
            {row.status !== 'rejected' && (
              <button onClick={onReject} className="px-3 py-1 rounded-lg bg-rose-600/30 text-rose-400 mr-auto">ارفض</button>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-2 text-sm">
          <Field label="الإفيه"><textarea rows={2} value={draft.line_ar} onChange={(e) => setDraft({ ...draft, line_ar: e.target.value })} /></Field>
          <Field label="سياق (اختياري)"><input value={draft.context_ar ?? ''} onChange={(e) => setDraft({ ...draft, context_ar: e.target.value || null })} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="الفيلم/المسلسل"><input value={draft.movie_title_ar} onChange={(e) => setDraft({ ...draft, movie_title_ar: e.target.value })} /></Field>
            <Field label="السنة"><input type="number" value={draft.movie_year} onChange={(e) => setDraft({ ...draft, movie_year: parseInt(e.target.value, 10) })} /></Field>
            <Field label="النوع">
              <select value={draft.movie_kind} onChange={(e) => setDraft({ ...draft, movie_kind: e.target.value })}>
                <option value="film">فيلم</option>
                <option value="series">مسلسل</option>
                <option value="play">مسرحية</option>
              </select>
            </Field>
            <Field label="الممثل"><input value={draft.actor_name_ar} onChange={(e) => setDraft({ ...draft, actor_name_ar: e.target.value })} /></Field>
            <Field label="الشخصية"><input value={draft.character_name_ar ?? ''} onChange={(e) => setDraft({ ...draft, character_name_ar: e.target.value || null })} /></Field>
            <Field label="درجة الحنين 1-10"><input type="number" min={1} max={10} value={draft.nostalgia_score ?? ''} onChange={(e) => setDraft({ ...draft, nostalgia_score: e.target.value ? parseInt(e.target.value, 10) : null })} /></Field>
          </div>
          <Field label="المصدر"><input value={draft.source_url} onChange={(e) => setDraft({ ...draft, source_url: e.target.value })} /></Field>
          <div className="flex gap-2 pt-2">
            <button onClick={save} className="px-4 py-2 rounded-lg bg-amber-500 text-ink-900">حفظ</button>
            <button onClick={() => { setDraft(row); setEdit(false) }} className="px-4 py-2 rounded-lg bg-ink-700 text-ink-300">إلغاء</button>
          </div>
        </div>
      )}
    </div>
  )
}

function CreateNew({ pw, onDone }: { pw: string; onDone: () => void }) {
  const [d, setD] = useState({
    line_ar: '',
    movie_title_ar: '',
    movie_year: new Date().getFullYear() - 10,
    actor_name_ar: '',
    character_name_ar: '',
    source_url: 'https://',
    status: 'playable' as 'candidate' | 'playable',
  })
  const [busy, setBusy] = useState(false)
  async function save() {
    if (!d.line_ar || !d.movie_title_ar || !d.actor_name_ar) { alert('املأ الحقول المطلوبة'); return }
    setBusy(true)
    const res = await fetch('/api/curator/effehat', {
      method: 'POST',
      headers: { 'x-curator-password': pw, 'content-type': 'application/json' },
      body: JSON.stringify(d),
    })
    setBusy(false)
    if (res.ok) onDone()
    else alert('فشل: ' + (await res.text()))
  }
  return (
    <div className="space-y-2 text-sm">
      <Field label="الإفيه *"><textarea rows={2} value={d.line_ar} onChange={(e) => setD({ ...d, line_ar: e.target.value })} /></Field>
      <Field label="الفيلم/المسلسل *"><input value={d.movie_title_ar} onChange={(e) => setD({ ...d, movie_title_ar: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="السنة *"><input type="number" value={d.movie_year} onChange={(e) => setD({ ...d, movie_year: parseInt(e.target.value, 10) })} /></Field>
        <Field label="الممثل *"><input value={d.actor_name_ar} onChange={(e) => setD({ ...d, actor_name_ar: e.target.value })} /></Field>
      </div>
      <Field label="الشخصية"><input value={d.character_name_ar} onChange={(e) => setD({ ...d, character_name_ar: e.target.value })} /></Field>
      <Field label="المصدر *"><input value={d.source_url} onChange={(e) => setD({ ...d, source_url: e.target.value })} /></Field>
      <Field label="الحالة">
        <select value={d.status} onChange={(e) => setD({ ...d, status: e.target.value as 'candidate' | 'playable' })}>
          <option value="playable">منشور (يظهر في اللعبة)</option>
          <option value="candidate">مسودة</option>
        </select>
      </Field>
      <button disabled={busy} onClick={save} className="w-full mt-3 py-3 rounded-full bg-amber-500 text-ink-900 font-bold disabled:opacity-50">حفظ</button>
    </div>
  )
}

function ImportCsv({ pw, onDone }: { pw: string; onDone: () => void }) {
  const [csv, setCsv] = useState(
    'line_ar,movie_title_ar,movie_year,actor_name_ar,source_url\n',
  )
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ inserted: number; skipped: number; errored: number; total: number } | null>(null)

  async function go() {
    setBusy(true)
    const res = await fetch('/api/curator/import-csv', {
      method: 'POST',
      headers: { 'x-curator-password': pw, 'content-type': 'application/json' },
      body: JSON.stringify({ csv }),
    })
    setBusy(false)
    if (res.ok) {
      const r = await res.json() as { inserted: number; skipped: number; errored: number; total: number }
      setResult(r)
      setTimeout(onDone, 1500)
    } else {
      alert('فشل: ' + (await res.text()))
    }
  }

  return (
    <div className="space-y-2 text-sm">
      <p className="text-ink-300 text-xs">
        أول صف عناوين الأعمدة. الأعمدة المطلوبة: <code>line_ar, movie_title_ar, movie_year, actor_name_ar, source_url</code>. كلهم بيتسجلوا كمرشحين.
      </p>
      <textarea
        rows={12}
        dir="ltr"
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        className="font-mono text-xs"
      />
      <button disabled={busy} onClick={go} className="w-full mt-3 py-3 rounded-full bg-amber-500 text-ink-900 font-bold disabled:opacity-50">
        {busy ? '…' : 'استورد'}
      </button>
      {result && (
        <p className="text-leaf-400 text-sm">
          ✓ تم إضافة {result.inserted} / تجاهل {result.skipped} / خطأ {result.errored} من أصل {result.total}
        </p>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-ink-500 block mb-1">{label}</span>
      <div className="[&_input]:w-full [&_input]:px-3 [&_input]:py-2 [&_input]:rounded-lg [&_input]:bg-ink-900 [&_input]:border [&_input]:border-ink-700 [&_input]:text-ink-50 [&_textarea]:w-full [&_textarea]:px-3 [&_textarea]:py-2 [&_textarea]:rounded-lg [&_textarea]:bg-ink-900 [&_textarea]:border [&_textarea]:border-ink-700 [&_textarea]:text-ink-50 [&_select]:w-full [&_select]:px-3 [&_select]:py-2 [&_select]:rounded-lg [&_select]:bg-ink-900 [&_select]:border [&_select]:border-ink-700 [&_select]:text-ink-50">
        {children}
      </div>
    </label>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-ink-700/40 border border-ink-700 px-3 py-3">
      <div className="text-2xl font-bold text-amber-300">{value}</div>
      <div className="text-xs text-ink-500">{label}</div>
    </div>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-ink-900/80 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-ink-900 border border-ink-700 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-ink-700 flex items-center justify-between">
          <h2 className="text-amber-300">{title}</h2>
          <button onClick={onClose} className="text-ink-500 text-xl px-2">×</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-ink-700/50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎬</span>
            <span className="font-display text-xl text-amber-300">إفيه — لوحة الكيوريتور</span>
          </div>
          <a href="/" className="text-xs text-amber-300">→ اللعبة</a>
        </div>
      </header>
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-6">{children}</main>
    </div>
  )
}
